import React                       from 'react';
import { createPortal }            from 'react-dom';
import { observer }                from 'mobx-react-lite';
import onboardingStore             from '@/TokamapEditor/stores/onboardingStore';
import type { OnboardingTarget }   from '@/TokamapEditor/stores/onboardingStore';
import { ONBOARDING_TARGET_CLASS } from '@/TokamapEditor/stores/onboardingStore';
import currentEditorStore          from '@/TokamapEditor/stores/currentEditorStore';
import useDesignTokens             from '@/hooks/useDesignTokens';

export const ONBOARDING_CARD_CLASS = 'onboarding-card';

const HOLE_PADDING = 6;
const HOLE_RADIUS = 10;
const VEIL_Z_INDEX = 1040;

const OPEN_POPOVERS = '.ant-popover:not(.ant-popover-hidden)';

const OPEN_ZONES: Partial<Record<OnboardingTarget, string[]>> = {
	draw: [`${OPEN_POPOVERS}:has(video) .ant-popover-inner`],
	color: [`${OPEN_POPOVERS} .ant-popover-inner`],
};

type Hole = { x: number; y: number; width: number; height: number };

const contains = (outer: Hole, inner: Hole) => outer !== inner
	&& outer.x <= inner.x && outer.y <= inner.y
	&& outer.x + outer.width >= inner.x + inner.width
	&& outer.y + outer.height >= inner.y + inner.height;

const withoutNested = (holes: Hole[]) => holes.filter(hole => !holes.some(other => contains(other, hole)));

const holesFor = (target: OnboardingTarget): Hole[] => withoutNested([
	`.${ONBOARDING_TARGET_CLASS}`,
	`.${ONBOARDING_CARD_CLASS}`,
	...(OPEN_ZONES[target] ?? []),
]
	.flatMap(selector => [...document.querySelectorAll(selector)])
	.map(element => element.getBoundingClientRect())
	.filter(rect => rect.width && rect.height)
	.map(rect => ({
		x: Math.round(rect.left - HOLE_PADDING),
		y: Math.round(rect.top - HOLE_PADDING),
		width: Math.round(rect.width + HOLE_PADDING * 2),
		height: Math.round(rect.height + HOLE_PADDING * 2),
	})));

const roundedRect = ({ x, y, width, height }: Hole) => {
	const radius = Math.min(HOLE_RADIUS, width / 2, height / 2);

	return `M${x + radius} ${y} H${x + width - radius} Q${x + width} ${y} ${x + width} ${y + radius} `
		+ `V${y + height - radius} Q${x + width} ${y + height} ${x + width - radius} ${y + height} `
		+ `H${x + radius} Q${x} ${y + height} ${x} ${y + height - radius} V${y + radius} Q${x} ${y} ${x + radius} ${y} Z`;
};

const isTyping = (target: EventTarget | null) => target instanceof HTMLElement
	&& (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);

const isUndo = (event: KeyboardEvent) => (event.ctrlKey || event.metaKey) && !event.shiftKey && 'z' === event.key.toLowerCase();

const modalOpen = () => [...document.querySelectorAll('.ant-modal-wrap')].some(wrap => 'none' !== getComputedStyle(wrap).display);

const OnboardingFocus = observer(() => {
	const tokens = useDesignTokens();
	const target = onboardingStore.target;
	const drawing = currentEditorStore._isDrawing;
	const active = !!target && !drawing;
	const [holes, setHoles] = React.useState<Hole[] | null>(null);

	React.useEffect(() => {
		if (!active || !target) {
			setHoles(null);

			return;
		}

		let frame = 0;

		const track = () => {
			const next = modalOpen() ? null : holesFor(target);

			setHoles(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
			frame = requestAnimationFrame(track);
		};

		track();

		return () => cancelAnimationFrame(frame);
	}, [active, target]);

	const veiled = !!holes;

	React.useEffect(() => {
		if (!veiled) return;

		const swallow = (event: KeyboardEvent) => {
			if (isTyping(event.target) || ('undo' === target && isUndo(event))) return;

			event.stopImmediatePropagation();
		};

		window.addEventListener('keydown', swallow, true);

		return () => window.removeEventListener('keydown', swallow, true);
	}, [veiled, target]);

	if (!holes) return null;

	const screen = `M0 0 H${window.innerWidth} V${window.innerHeight} H0 Z`;

	return createPortal(
		<svg
			width="100%"
			height="100%"
			style={{ position: 'fixed', inset: 0, zIndex: VEIL_Z_INDEX, pointerEvents: 'none' }}
		>
			<path
				d={[screen, ...holes.map(roundedRect)].join(' ')}
				fillRule="evenodd"
				fill={`color-mix(in srgb, ${tokens.nav.bg} 45%, transparent)`}
				style={{ pointerEvents: 'visiblePainted' }}
			/>
		</svg>,
		document.body,
	);
});

export default OnboardingFocus;
